/**
 * API Service
 *
 * Typed client for the Sherpai admin API.
 * Covers both vendor analytics (your team) and tenant analytics (IT admins).
 */

import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import type { DiscountInput, PricingTier, VolumeDiscountBand } from '../../../shared/src/pricing';
export { protectedResources } from '../config/authConfig';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Get the shared API client instance (for use outside React components).
 * The MSAL interceptor is set up by useApiClient() in a React component.
 */
export function getApiClient() {
  return api;
}

/**
 * Hook to get an authenticated API client.
 * Call once per component — the interceptor is added once.
 */
// Single interceptor installed once — avoids stacking on every render
let interceptorInstalled = false;
let msalAccounts: any[] = [];

export function useApiClient() {
  const { accounts } = useMsal();

  // Keep reference current so the interceptor always sees the latest account
  msalAccounts = accounts;

  if (!interceptorInstalled) {
    interceptorInstalled = true;
    // Send the Entra tenant ID and user's home account ID as custom headers.
    // Both are synchronously available on the AccountInfo object — no async
    // token acquisition needed. The API reads X-Entra-Tenant-Id to scope queries.
    api.interceptors.request.use(
      (config) => {
        const account = msalAccounts[0];
        if (account) {
          config.headers['X-Entra-Tenant-Id'] = account.tenantId;
          config.headers['X-Account-Id'] = account.homeAccountId;
          if (account.name) config.headers['X-User-Name'] = account.name;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  return api;
}

// ─── Vendor Analytics (your team) ─────────────────────────────────────────────

export const analyticsService = {

  // Summary KPIs across all tenants
  async getVendorKPIs(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/vendor/kpis', { params: { days: periodDays } });
    return data;
  },

  // Owner dashboard KPIs (extended metrics)
  async getOwnerKPIs(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/owner/kpis', { params: { days: periodDays } });
    return data;
  },

  // Cross-tenant demographics: roles, industries, countries, org sizes, seniorities
  async getDemographics(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/owner/demographics', { params: { days: periodDays } });
    return data;
  },

  // Insights: questions, problems, outcomes, product signals
  async getInsights(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/owner/insights', { params: { days: periodDays } });
    return data;
  },

  // Questions broken down by role type
  async getRoleBreakdown(periodDays: string): Promise<Array<{ role_type: string; count: number }>> {
    const { data } = await api.get('/analytics/vendor/roles', { params: { days: periodDays } });
    return data;
  },

  // Questions broken down by category
  async getCategoryBreakdown(periodDays: string): Promise<Array<{ question_category: string; count: number }>> {
    const { data } = await api.get('/analytics/vendor/categories', { params: { days: periodDays } });
    return data;
  },

  // Questions broken down by industry
  async getIndustryBreakdown(periodDays: string): Promise<Array<{ industry: string; count: number }>> {
    const { data } = await api.get('/analytics/vendor/industries', { params: { days: periodDays } });
    return data;
  },

  // Envai and ProcessAI product interest signals
  async getProductSignals(periodDays: string): Promise<{
    envaiSignals: number;
    envaiClicks: number;
    processaiSignals: number;
    processaiClicks: number;
  }> {
    const { data } = await api.get('/analytics/vendor/product-signals', { params: { days: periodDays } });
    return data;
  },

  // ─── Tenant Analytics (IT admin view) ───────────────────────────────────────

  // KPIs for the calling admin's tenant
  async getTenantKPIs(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/tenant/kpis', { params: { days: periodDays } });
    return data;
  },

  // Top question topics for the calling admin's tenant
  async getTenantTopics(periodDays: string): Promise<Array<{ question_category: string; count: number }>> {
    const { data } = await api.get('/analytics/tenant/topics', { params: { days: periodDays } });
    return data;
  },

  // Tenant IT intelligence: adoption and activity hotspots
  async getTenantHotspots(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/tenant/hotspots', { params: { days: periodDays } });
    return data;
  },

  // Tenant IT intelligence: demand that may require policy/licence upgrades
  async getTenantUpgradeDemand(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/tenant/upgrade-demand', { params: { days: periodDays } });
    return data;
  },

  // Tenant IT intelligence: support/risk pressure areas
  async getTenantRiskSupport(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/tenant/risk-support', { params: { days: periodDays } });
    return data;
  },

  // Tenant IT intelligence: measurable impact and value
  async getTenantImpact(periodDays: string): Promise<any> {
    const { data } = await api.get('/analytics/tenant/impact', { params: { days: periodDays } });
    return data;
  },

  // ─── Legacy endpoints (kept for backwards compat) ───────────────────────────

  async getSummaryKPIs(startDate: string, endDate: string): Promise<any> {
    const { data } = await api.get('/analytics/summary', { params: { startDate, endDate } });
    return data;
  },

  async getTierDistribution(startDate: string, endDate: string): Promise<any[]> {
    const { data } = await api.get('/analytics/tiers', { params: { startDate, endDate } });
    return data;
  },
};

// ─── Tenant Management ─────────────────────────────────────────────────────────

export const tenantService = {

  async onboard(payload: {
    company_name: string;
    industry: string;
    org_size_bracket: string;
    it_policy: object;
    entra_tenant_id: string;
  }): Promise<{ tenant_id: string; status: string }> {
    const { data } = await api.post('/tenants/onboard', payload);
    return data;
  },

  async updatePolicy(tenantId: string, itPolicy: object): Promise<void> {
    await api.patch(`/tenants/${tenantId}/policy`, { it_policy: itPolicy });
  },

  async getSubscription(tenantId: string): Promise<TenantSubscriptionInfo> {
    const { data } = await api.get(`/tenants/${tenantId}/subscription`);
    return data;
  },

  // List all tenants with usage metrics (for owner view)
  async listWithUsage(periodDays: string): Promise<any[]> {
    const { data } = await api.get('/tenants/with-usage', { params: { days: periodDays } });
    return data;
  },

  async updateTenant(tenantId: string, updates: {
    subscription_tier?: string;
    licensed_users?: number;
    billing_start_date?: string | null;
    tenant_name?: string;
    org_industry?: string;
    customer_group_id?: string | null;
  }): Promise<void> {
    await api.patch(`/tenants/${tenantId}`, updates);
  },

  async updateTenantTrial(tenantId: string, updates: {
    trial_ends_at?: string | null;
    trial_max_users?: number;
    subscription_tier?: string;
    trial_accepted_at?: string | null;
  }): Promise<any> {
    const { data } = await api.patch(`/tenants/${tenantId}/trial`, updates);
    return data;
  },
};

// ─── Customer Self-Service ────────────────────────────────────────────────────

export interface TenantRecord {
  tenant_id: string;
  tenant_name: string;
  org_industry: string | null;
  org_size_bracket: string | null;
  org_country: string | null;
  org_state_region: string | null;
  org_context: string | null;
  is_access_restricted: boolean;
  it_policy: {
    supports_m365_copilot?: boolean;
    supports_copilot_studio?: boolean;
    supports_azure_ai?: boolean;
    org_notes?: string | null;
  };
  subscription_tier: string;
  is_active: boolean;
  created_at?: string;
}

export interface CustomerStats {
  activeUsers: number;
  totalConversations: number;
  topCategories: Array<{ category: string; count: number }>;
  roleBreakdown: Array<{ role: string; count: number }>;
  goalBreakdown: Array<{ goal: string; count: number }>;
}

export interface InvoiceSummary {
  id: string;
  periodNumber: number;
  periodStart: string;
  periodEnd: string;
  activeUsers: number;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'overdue';
  generatedAt: string;
  paidAt: string | null;
}

export interface BillingPeriodInfo {
  periodNumber: number;
  start: string;
  end: string;
}

export interface PricingOfferSummary {
  source: 'group' | 'promotion' | 'discount_code';
  id: string | null;
  name: string;
  startsAt: string | null;
  endsAt: string | null;
  bandDiscounts: VolumeDiscountBand[];
  invoiceDiscount: DiscountInput | null;
}

export interface TenantSubscriptionInfo {
  tier: string;
  status: string;
  trialEndsAt: string | null;
  trialMaxUsers?: number;
  activeUsers: number;
  licensedUsers: number;
  conversationsUsed: number;
  storageUsedMB: number;
  maxStorageMB: number;
  invoices?: InvoiceSummary[];
  activeUsersHistory?: number[];
  pricingTiers?: PricingTier[];
  effectivePricingTiers?: PricingTier[];
  invoiceDiscounts?: DiscountInput[];
  appliedOffers?: PricingOfferSummary[];
  billingAnchor?: string;
  currentPeriod?: BillingPeriodInfo | null;
}

export const customerService = {
  async getMe(): Promise<TenantRecord> {
    const { data } = await api.get('/customer/me');
    return data;
  },

  async onboard(payload: {
    company_name: string;
    domain?: string;
    industry: string;
    org_size_bracket: string;
    org_country: string;
    org_state_region?: string;
    it_policy: object;
    org_context?: string;
    admin_name?: string;
    admin_email?: string;
  }): Promise<{ tenant_id: string }> {
    const { data } = await api.post('/customer/onboard', payload);
    return data;
  },

  async updateSettings(payload: Partial<TenantRecord & { it_policy: object }>): Promise<void> {
    await api.put('/customer/settings', payload);
  },

  async getStats(): Promise<CustomerStats> {
    const { data } = await api.get('/customer/stats');
    return data;
  },

  async getSubscription(): Promise<TenantSubscriptionInfo> {
    const { data } = await api.get('/customer/subscription');
    return data;
  },
};

// ─── Customer Groups ──────────────────────────────────────────────────────

export const customerGroupService = {
  async list(): Promise<any[]> {
    const { data } = await api.get('/customer-groups');
    return data;
  },

  async create(payload: {
    name: string;
    description?: string | null;
    pricing_override?: any;
    discount_pct?: number;
  }): Promise<{ id: string; name: string }> {
    const { data } = await api.post('/customer-groups', payload);
    return data;
  },

  async update(id: string, updates: Partial<{
    name: string;
    description: string | null;
    pricing_override: any;
    discount_pct: number;
    is_active: boolean;
  }>): Promise<void> {
    await api.patch(`/customer-groups/${id}`, updates);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/customer-groups/${id}`);
  },
};

// ─── Promotions ───────────────────────────────────────────────────────────

export const promotionService = {
  async list(): Promise<PromotionRecord[]> {
    const { data } = await api.get('/promotions');
    return data;
  },

  async create(payload: {
    name: string;
    description?: string | null;
    discount_type?: 'percentage' | 'fixed_amount' | null;
    discount_value?: number | null;
    starts_at: string;
    ends_at: string;
    applies_to?: string;
    target_id?: string | null;
    max_redemptions?: number | null;
    band_discounts?: VolumeDiscountBand[];
  }): Promise<{ id: string; name: string }> {
    const { data } = await api.post('/promotions', payload);
    return data;
  },

  async update(id: string, updates: Record<string, any>): Promise<void> {
    await api.patch(`/promotions/${id}`, updates);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/promotions/${id}`);
  },
};

export interface PromotionRecord {
  id: string;
  name: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed_amount' | null;
  discount_value: number | null;
  starts_at: string;
  ends_at: string;
  applies_to: 'all' | 'group' | 'tenant';
  target_id: string | null;
  max_redemptions: number | null;
  redemption_count: number;
  is_active: boolean;
  created_at: string;
  band_discounts?: VolumeDiscountBand[];
}

export const pricingService = {
  async getConfig(): Promise<PricingTier[]> {
    const { data } = await api.get('/pricing-config');
    return data;
  },
};

// ─── Discount Codes ───────────────────────────────────────────────────────

export const discountCodeService = {
  async list(): Promise<any[]> {
    const { data } = await api.get('/discount-codes');
    return data;
  },

  async create(payload: {
    code: string;
    description?: string | null;
    discount_type: string;
    discount_value: number;
    duration_months: number;
    valid_from: string;
    valid_until: string;
    max_uses?: number | null;
  }): Promise<{ id: string; code: string }> {
    const { data } = await api.post('/discount-codes', payload);
    return data;
  },

  async update(id: string, updates: Record<string, any>): Promise<void> {
    await api.patch(`/discount-codes/${id}`, updates);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/discount-codes/${id}`);
  },

  async redeem(code: string): Promise<{ success: boolean; expires_at: string }> {
    const { data } = await api.post(`/discount-codes/${encodeURIComponent(code)}/redeem`, {});
    return data;
  },
};

// ─── Deployment Management ──────────────────────────────────────────────────

export interface Deployment {
  deployment_id: string;
  tenant_id: string;
  tenant_name: string;
  domain: string;
  instance_name: string;
  environment: string;
  status: string;
  version: string;
  region: string;
  resource_group: string | null;
  app_service_url: string | null;
  bot_app_id: string | null;
  search_endpoint: string | null;
  key_vault_name: string | null;
  last_deployed_at: string | null;
  last_deploy_log: string | null;
  deployed_by: string | null;
  subscription_tier?: string;
  org_industry?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateDeploymentPayload {
  instance_name: string;
  environment: string;
  region: string;
  tenant_id?: string;
  // Inline tenant creation
  tenant_name?: string;
  domain?: string;
  industry?: string;
  org_size_bracket?: string;
  subscription_tier?: string;
}

export const deploymentService = {

  async list(status?: string): Promise<Deployment[]> {
    const params = status ? { status } : undefined;
    const { data } = await api.get('/deployments', { params });
    return data;
  },

  async get(id: string): Promise<Deployment> {
    const { data } = await api.get(`/deployments/${id}`);
    return data;
  },

  async create(payload: CreateDeploymentPayload): Promise<{ deployment_id: string; tenant_id: string }> {
    const { data } = await api.post('/deployments', payload);
    return data;
  },

  async update(id: string, updates: Partial<Deployment>): Promise<void> {
    await api.patch(`/deployments/${id}`, updates);
  },

  async redeploy(id: string): Promise<void> {
    await api.post(`/deployments/${id}/redeploy`, {});
  },
};
